import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Container, Form, Spinner } from "react-bootstrap";
import {
    clearChatConversation,
    fetchChatConversations,
    fetchChatMessages,
    markChatMessagesRead,
    sendChatMessage,
} from "../api/api";
import { useUser } from "../UserContext";

const normalizeList = (data) => Array.isArray(data) ? data : (data?.results || []);
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";

const shortTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const dayLabel = (value) => {
    const date = new Date(value);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
};

const Chat = () => {
    const { user } = useUser();
    const [conversations, setConversations] = useState([]);
    const [selectedKey, setSelectedKey] = useState("");
    const [messages, setMessages] = useState([]);
    const [search, setSearch] = useState("");
    const [body, setBody] = useState("");
    const [isMemo, setIsMemo] = useState(false);
    const [loading, setLoading] = useState(true);
    const [threadLoading, setThreadLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");
    const threadEndRef = useRef(null);

    const selected = useMemo(
        () => conversations.find((conversation) => conversation.key === selectedKey),
        [conversations, selectedKey]
    );

    const filteredConversations = useMemo(() => {
        const query = search.trim().toLowerCase();
        return conversations.filter((conversation) => !query || conversation.name.toLowerCase().includes(query));
    }, [conversations, search]);

    const loadConversations = useCallback(async (chooseInitial = false) => {
        const data = await fetchChatConversations();
        const next = data?.conversations || [];
        setConversations(next);
        if (chooseInitial) {
            const initial = next.find((item) => item.unread_count > 0)
                || next.find((item) => !item.is_everyone && item.last_message_at)
                || next.find((item) => !item.is_everyone)
                || next[0];
            if (initial && window.innerWidth > 767) setSelectedKey(initial.key);
        }
        return next;
    }, []);

    const loadThread = useCallback(async (key, quiet = false) => {
        if (!key) return;
        if (!quiet) setThreadLoading(true);
        try {
            const data = await fetchChatMessages(key);
            setMessages(normalizeList(data));
            await markChatMessagesRead(key);
            await loadConversations(false);
            setError("");
        } catch (err) {
            console.error("Error loading chat thread:", err);
            setError("Could not load this conversation.");
        } finally {
            if (!quiet) setThreadLoading(false);
        }
    }, [loadConversations]);

    useEffect(() => {
        const start = async () => {
            try {
                await loadConversations(true);
            } catch (err) {
                console.error("Error loading conversations:", err);
                setError("Could not load in-house chat.");
            } finally {
                setLoading(false);
            }
        };
        start();
    }, [loadConversations]);

    useEffect(() => {
        setBody("");
        setIsMemo(false);
        if (selectedKey) loadThread(selectedKey);
    }, [loadThread, selectedKey]);

    useEffect(() => {
        const interval = setInterval(async () => {
            try {
                await loadConversations(false);
                if (selectedKey) await loadThread(selectedKey, true);
            } catch (err) {
                console.error("Error refreshing chat:", err);
            }
        }, 20000);
        return () => clearInterval(interval);
    }, [loadConversations, loadThread, selectedKey]);

    useEffect(() => {
        threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSubmit = async (event) => {
        event?.preventDefault();
        if (!body.trim() || !selected || !selected.can_send) return;
        try {
            setSending(true);
            setError("");
            await sendChatMessage({
                recipient: selected.is_everyone ? null : selected.peer_id,
                body: body.trim(),
                to_all: selected.is_everyone,
                message_kind: isMemo ? "MEMO" : "MESSAGE",
            });
            setBody("");
            setIsMemo(false);
            await loadThread(selected.key);
        } catch (err) {
            console.error("Error sending message:", err);
            setError(err.response?.data?.detail || err.response?.data?.body || "Could not send the message.");
        } finally {
            setSending(false);
        }
    };

    const handleClear = async () => {
        if (!selected?.can_clear) return;
        const warning = selected.is_everyone
            ? "Clear the Everyone and memo history for every user? This hides all shared messages sent so far."
            : `Clear your view of the conversation with ${selected.name}? Their copy will remain available.`;
        if (!window.confirm(warning)) return;
        try {
            await clearChatConversation(selected.key);
            setMessages([]);
            await loadConversations(false);
        } catch (err) {
            setError(err.response?.data?.detail || "Could not clear this conversation.");
        }
    };

    const handleComposerKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    };

    if (loading) {
        return <div className="chat-loading"><Spinner animation="border" /></div>;
    }

    let previousDay = "";
    return (
        <Container fluid className="chat-page py-3 py-lg-4">
            <div className={`messenger-shell ${selectedKey ? "mobile-thread-open" : ""}`}>
                <aside className="conversation-panel">
                    <div className="conversation-panel-header">
                        <div><div className="chat-eyebrow">TEAM SPACE</div><h1>Messages</h1></div>
                        <span className="chat-online-dot" title="Chat refreshes automatically" />
                    </div>
                    <div className="conversation-search">
                        <i className="bi bi-search" />
                        <Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search people" aria-label="Search conversations" />
                    </div>
                    <div className="conversation-list">
                        {filteredConversations.map((conversation) => (
                            <button type="button" key={conversation.key} className={`conversation-row ${selectedKey === conversation.key ? "active" : ""}`} onClick={() => setSelectedKey(conversation.key)}>
                                <span className={`chat-avatar ${conversation.is_everyone ? "everyone" : ""}`}>
                                    {conversation.is_everyone ? <i className="bi bi-megaphone-fill" /> : initials(conversation.name)}
                                </span>
                                <span className="conversation-copy">
                                    <span className="conversation-name-line"><strong>{conversation.name}</strong><small>{shortTime(conversation.last_message_at)}</small></span>
                                    <span className="conversation-preview-line">
                                        <span>{conversation.last_message || (conversation.is_everyone ? "Company announcements" : "Start a conversation")}</span>
                                        {conversation.unread_count > 0 && <Badge pill bg="success">{conversation.unread_count}</Badge>}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                </aside>

                <section className="message-panel">
                    {selected ? (
                        <>
                            <header className="message-header">
                                <Button variant="link" className="chat-back-button" onClick={() => setSelectedKey("")} aria-label="Back to conversations"><i className="bi bi-arrow-left" /></Button>
                                <span className={`chat-avatar ${selected.is_everyone ? "everyone" : ""}`}>
                                    {selected.is_everyone ? <i className="bi bi-megaphone-fill" /> : initials(selected.name)}
                                </span>
                                <div className="message-header-copy"><strong>{selected.name}</strong><small>{selected.is_everyone ? "Shared announcements and memos" : "Private conversation"}</small></div>
                                {selected.can_clear && <Button variant="link" className="chat-clear-button" onClick={handleClear} title="Clear conversation"><i className="bi bi-trash3" /> <span>Clear</span></Button>}
                            </header>

                            {error && <Alert variant="danger" className="chat-alert">{error}</Alert>}
                            <div className="message-scroll">
                                {threadLoading ? (
                                    <div className="chat-thread-state"><Spinner animation="border" size="sm" /> Loading conversation…</div>
                                ) : messages.length === 0 ? (
                                    <div className="empty-chat-state">
                                        <span className="empty-chat-icon"><i className="bi bi-chat-heart" /></span>
                                        <h2>No messages here yet</h2>
                                        <p>{selected.can_send ? "Send the first message below." : "Company announcements will appear here."}</p>
                                    </div>
                                ) : messages.map((message) => {
                                    const dateLabel = dayLabel(message.created_at);
                                    const showDay = dateLabel !== previousDay;
                                    previousDay = dateLabel;
                                    const outgoing = String(message.sender) === String(user?.user_id || user?.id);
                                    return (
                                        <React.Fragment key={message.id}>
                                            {showDay && <div className="chat-day-divider"><span>{dateLabel}</span></div>}
                                            <div className={`message-line ${outgoing ? "outgoing" : "incoming"}`}>
                                                <div className={`message-bubble ${message.message_kind === "MEMO" ? "memo" : ""}`}>
                                                    {selected.is_everyone && <div className="message-sender">{message.message_kind === "MEMO" && <i className="bi bi-pin-angle-fill" />}{message.sender_name}{message.message_kind === "MEMO" ? " · Memo" : ""}</div>}
                                                    <div className="message-body">{message.body}</div>
                                                    <div className="message-time">
                                                        {new Date(message.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                                                        {outgoing && <i className="bi bi-check2-all" />}
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                                <div ref={threadEndRef} />
                            </div>

                            {selected.can_send ? (
                                <Form className="message-composer" onSubmit={handleSubmit}>
                                    {selected.is_everyone && <Form.Check type="switch" id="memo-switch" label="Memo" checked={isMemo} onChange={(event) => setIsMemo(event.target.checked)} title="Pin this as a formal company memo" />}
                                    <Form.Control as="textarea" rows={1} value={body} onChange={(event) => setBody(event.target.value)} onKeyDown={handleComposerKeyDown} placeholder={isMemo ? "Write a company memo…" : `Message ${selected.name}…`} aria-label="Message" />
                                    <Button type="submit" disabled={sending || !body.trim()} className="chat-send-button" aria-label="Send message">
                                        {sending ? <Spinner animation="border" size="sm" /> : <i className="bi bi-send-fill" />}
                                    </Button>
                                </Form>
                            ) : (
                                <div className="shared-readonly-note"><i className="bi bi-shield-lock" /> Only administrators can post or clear shared messages and memos.</div>
                            )}
                        </>
                    ) : (
                        <div className="empty-chat-state desktop-empty"><span className="empty-chat-icon"><i className="bi bi-chat-square-text" /></span><h2>Select a conversation</h2><p>Choose a colleague or open Everyone & memos.</p></div>
                    )}
                </section>
            </div>
        </Container>
    );
};

export default Chat;
